import { IServices, TypeServiceEnum } from 'features/salesOrder/types';
import { IInvoice } from './types';

/**
 * Representa un servicio de la orden con su monto de venta, cuánto ya fue
 * facturado (en facturas DRAFT + ISSUED, incluyendo NCs que tienen total < 0)
 * y cuánto queda pendiente de facturar.
 */
export interface ISaleOrderBillableService {
	serviceId: string;
	serviceType: TypeServiceEnum;
	/** Etiqueta corta: tipo + referencia */
	label: string;
	currency: string;
	totalSaleAmount: number;
	/** Monto en moneda local (ej: COP para Colombia). Presente solo cuando corresponde. */
	localCurrencyAmount?: number;
	alreadyBilledAmount: number;
	/** totalSaleAmount − alreadyBilledAmount */
	pendingAmount: number;
	/**
	 * Texto informativo de detalle para mostrar al operador en el drawer
	 * (hotel, rutas, fechas, pax). NUNCA incluye notas internas.
	 */
	adenda: string;
	/** IVA sugerido según BU/servicio/ruta. Editable por el operador. */
	suggestedTaxRate: number;
	/** Proporción del importe que es base imponible (1 = base completa; 0.25 vuelos PY). */
	suggestedTaxableBaseRatio: number;
}

export interface IComputeBillableOptions {
	/** keyId → nombre completo, para incluir los pax de cada servicio en la adenda. */
	passengerNameById?: Record<string, string>;
	/** BU actual, para sugerir el IVA por país. */
	agencyId?: number;
}

const SERVICE_TYPE_NAMES: Partial<Record<TypeServiceEnum, string>> = {
	[TypeServiceEnum.Flight]: 'Vuelos',
	[TypeServiceEnum.FlightAncillary]: 'Adicionales de vuelo',
	[TypeServiceEnum.Accommodation]: 'Alojamiento',
	[TypeServiceEnum.Transfer]: 'Traslados',
	[TypeServiceEnum.Insurance]: 'Seguro de viaje',
	[TypeServiceEnum.Circuit]: 'Circuito',
	[TypeServiceEnum.CarRental]: 'Alquiler de auto',
	[TypeServiceEnum.Bus]: 'Bus',
	[TypeServiceEnum.Train]: 'Tren',
	[TypeServiceEnum.Activity]: 'Actividades',
	[TypeServiceEnum.FlightAddOn]: 'Adicionales de vuelo',
	[TypeServiceEnum.ThirdPartyPackage]: 'Paquetes de terceros',
	[TypeServiceEnum.Ferry]: 'Ferry',
	[TypeServiceEnum.Cruise]: 'Crucero',
	[TypeServiceEnum.Other]: 'Otros servicios',
	[TypeServiceEnum.Fee]: 'Fee',
};

const round2 = (v: number): number => Number((Number(v) || 0).toFixed(2));

const COLOMBIA_AGENCY_ID = 2;
const PARAGUAY_AGENCY_ID = 3;
const TN_ITAU_PY_AGENCY_ID = 5;

/** Aeropuertos de Paraguay: Asunción y Ciudad del Este (Guaraní). */
const PY_AIRPORT_CODES = new Set(['ASU', 'AGT']);

const COLOMBIA_FEE_TAX_RATE = 19;
const PY_SERVICE_TAX_RATE = 10;
/**
 * Vuelos que tocan PY: IVA 10% sobre base imponible reducida al 25% del pasaje
 * (efectivo 2.5% del total). No es una tasa de 2.5% sobre base completa.
 */
const PY_FLIGHT_TAX_RATE = 10;
const PY_FLIGHT_TAXABLE_BASE_RATIO = 0.25;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flightTouchesParaguay = (svc: any): boolean => {
	const legs: any[] = svc.flightLegs ?? [];
	return legs.some(
		(leg: any) =>
			PY_AIRPORT_CODES.has(leg?.departure?.airport?.code) || PY_AIRPORT_CODES.has(leg?.arrival?.airport?.code),
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const destinationIsAsuncion = (svc: any): boolean => {
	const code: string = svc.destination?.code ?? '';
	const name: string = svc.destination?.name ?? '';
	return code === 'ASU' || /asunci[oó]n/i.test(name);
};

export interface ISuggestedTax {
	rate: number;
	/** Proporción del importe que es base imponible (1 = base completa). */
	baseRatio: number;
}

const NO_TAX: ISuggestedTax = { rate: 0, baseRatio: 1 };

/**
 * IVA sugerido por BU:
 * - PY / Itaú PY: vuelos que tocan ASU/AGT → 10% sobre base reducida del 25%
 *   (efectivo 2.5% del total); si no tocan PY, exento.
 *   Otros servicios con destino Asunción → 10% base completa.
 * - Colombia: el IVA lo lleva solo el Fee (19%); el resto se factura como
 *   "ingresos para terceros" (0%).
 * Siempre es un default editable, nunca un valor forzado.
 */
const suggestTax = (entry: IServices, agencyId?: number): ISuggestedTax => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const svc = entry as any;

	if (agencyId === COLOMBIA_AGENCY_ID) {
		return entry.serviceType === TypeServiceEnum.Fee ? { rate: COLOMBIA_FEE_TAX_RATE, baseRatio: 1 } : NO_TAX;
	}

	if (agencyId === PARAGUAY_AGENCY_ID || agencyId === TN_ITAU_PY_AGENCY_ID) {
		if (entry.serviceType === TypeServiceEnum.Flight) {
			return flightTouchesParaguay(svc)
				? { rate: PY_FLIGHT_TAX_RATE, baseRatio: PY_FLIGHT_TAXABLE_BASE_RATIO }
				: NO_TAX;
		}
		return destinationIsAsuncion(svc) ? { rate: PY_SERVICE_TAX_RATE, baseRatio: 1 } : NO_TAX;
	}

	return NO_TAX;
};

const fmtDate = (iso?: string): string => {
	if (!iso) return '';
	try {
		return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('es-UY', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	} catch {
		return iso.slice(0, 10);
	}
};

/** Junta los ids de pax del servicio, venga como vengan según el tipo. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const collectPassengerIds = (svc: any): string[] => {
	const ids: string[] = [];

	if (Array.isArray(svc.passengerIds)) ids.push(...svc.passengerIds);

	const groups: any[] = svc.passengersGroupByPassengerType ?? [];
	for (const group of groups) {
		if (Array.isArray(group?.passengerIds)) ids.push(...group.passengerIds);
	}

	const rooms: any[] = svc.rooms ?? [];
	for (const room of rooms) {
		if (Array.isArray(room?.passengerIds)) ids.push(...room.passengerIds);
	}

	return Array.from(new Set(ids.filter(Boolean)));
};

/**
 * Construye el texto de adenda según el tipo de servicio.
 * NUNCA incluye internalNotes (son datos internos del operador, no del cliente).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractAdenda = (entry: IServices, passengerNameById?: Record<string, string>): string => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const svc = entry as any;
	const parts: string[] = [];

	switch (entry.serviceType) {
		case TypeServiceEnum.Accommodation: {
			if (svc.serviceProvider?.name) parts.push(svc.serviceProvider.name);
			if (svc.destination?.name) parts.push(svc.destination.name);
			if (svc.startDate) parts.push(`Check-in ${fmtDate(svc.startDate)}`);
			if (svc.endDate) parts.push(`Check-out ${fmtDate(svc.endDate)}`);
			const rooms: unknown[] = svc.rooms ?? [];
			if (rooms.length > 0) parts.push(`${rooms.length} hab.`);
			break;
		}
		case TypeServiceEnum.Transfer: {
			const segs: any[] = svc.transferSegments ?? [];
			if (segs.length > 0) {
				const seg = segs[0];
				if (seg.originName && seg.destinationName) {
					parts.push(`${seg.originName} → ${seg.destinationName}`);
				}
				if (seg.startDate) parts.push(fmtDate(seg.startDate));
			}
			break;
		}
		case TypeServiceEnum.Flight: {
			const legs: any[] = svc.flightLegs ?? [];
			if (legs.length > 0) {
				// Mostrar cada tramo individualmente: MVD→RIO | RIO→MVD
				const route = legs
					.map((leg: any) => {
						const orig: string = leg?.departure?.airport?.code ?? '';
						const dest: string = leg?.arrival?.airport?.code ?? '';
						return orig && dest ? `${orig}→${dest}` : '';
					})
					.filter(Boolean)
					.join(' | ');
				if (route) parts.push(route);
				if (legs[0]?.departure?.date) parts.push(fmtDate(legs[0].departure.date));
			}
			break;
		}
		case TypeServiceEnum.FlightAncillary:
		case TypeServiceEnum.FlightAddOn:
			break;
		default: {
			const provName: string = svc.serviceProvider?.fantasyName || svc.serviceProvider?.name || '';
			if (provName) parts.push(provName);
			if (svc.destination?.name) parts.push(svc.destination.name);
			break;
		}
	}

	// Pax del servicio: con nombres si los tenemos, si no solo la cantidad.
	const paxIds = collectPassengerIds(svc);
	if (paxIds.length > 0) {
		const names = passengerNameById ? paxIds.map((id) => passengerNameById[id]).filter(Boolean) : [];
		if (names.length > 0) {
			parts.push(`Pax: ${names.join(', ')}`);
		} else {
			parts.push(`${paxIds.length} pax`);
		}
	}

	return parts.filter(Boolean).join(' · ');
};

/**
 * La API devuelve los servicios como objetos planos (flat) — los campos
 * `totalSaleAmount`, `currency`, `reference`, etc. están directamente en
 * `entry`, no en un sub-array `entry.services[]`.
 */
const extractServiceInfo = (
	entry: IServices,
	options?: IComputeBillableOptions,
): { label: string; totalSaleAmount: number; localCurrencyAmount?: number; currency: string; adenda: string } => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const svc = entry as any;
	const typeLabel = SERVICE_TYPE_NAMES[entry.serviceType] ?? entry.serviceType;
	const detail: string =
		svc.reference && svc.reference !== '-' ? svc.reference : svc.serviceName ?? svc.packageName ?? '';
	const label = detail ? `${typeLabel} · ${detail}` : typeLabel;

	const rawAmount = svc.totalSaleAmount ?? svc.saleAmount ?? 0;
	const totalSaleAmount = round2(parseFloat(String(rawAmount)) || 0);
	const currency: string = svc.currency ?? 'USD';

	const rawLocal = svc.totalSaleAmountInLocalCurrency ?? svc.saleAmountInLocalCurrency;
	const localCurrencyAmount =
		rawLocal !== undefined && rawLocal !== null ? round2(parseFloat(String(rawLocal)) || 0) : undefined;
	const adenda = extractAdenda(entry, options?.passengerNameById);

	return { label, totalSaleAmount, localCurrencyAmount, currency, adenda };
};

/**
 * Calcula qué servicios de la orden todavía tienen saldo pendiente de facturar.
 *
 * Reglas:
 * - Se incluyen todas las facturas (DRAFT, ISSUED) porque bloquean el monto.
 * - Se suma totalInMainCurrency (USD), NUNCA item.total: ese está en la moneda
 *   de cada factura (COP en Colombia) y este motor razona todo en USD contra
 *   totalSaleAmount del servicio.
 * - Las NCs tienen items con montos negativos, lo que reduce el "ya facturado".
 * - Items sin serviceId no se computan (no reducen ningún servicio específico).
 * - Servicios deshabilitados (enable === false) se excluyen.
 */
export const computeBillableServices = (
	services: IServices[],
	invoices: IInvoice[],
	options?: IComputeBillableOptions,
): ISaleOrderBillableService[] => {
	const billedByServiceId: Record<string, number> = {};

	for (const invoice of invoices) {
		for (const item of invoice.items) {
			if (item.serviceId) {
				billedByServiceId[item.serviceId] = (billedByServiceId[item.serviceId] ?? 0) + item.totalInMainCurrency;
			}
		}
	}

	return services
		.filter((entry) => entry.enable !== false)
		.map((entry) => {
			const { label, totalSaleAmount, localCurrencyAmount, currency, adenda } = extractServiceInfo(entry, options);
			const alreadyBilledAmount = round2(billedByServiceId[entry.keyId] ?? 0);
			const pendingAmount = round2(totalSaleAmount - alreadyBilledAmount);
			const suggestedTax = suggestTax(entry, options?.agencyId);

			return {
				serviceId: entry.keyId,
				serviceType: entry.serviceType,
				label,
				currency,
				totalSaleAmount,
				localCurrencyAmount,
				alreadyBilledAmount,
				pendingAmount,
				adenda,
				suggestedTaxRate: suggestedTax.rate,
				suggestedTaxableBaseRatio: suggestedTax.baseRatio,
			};
		});
};

/** Tolerancia de redondeo al comparar facturado contra venta. */
const OVERINVOICED_TOLERANCE = 0.01;

export interface IOrderBillingSummary {
	/** Venta total de los servicios de la orden, en moneda principal. */
	total: number;
	/** Facturado neto de la orden, en moneda principal. */
	billed: number;
	/** Venta − facturado, nunca negativo. */
	pending: number;
	/** 0 a 100. */
	pct: number;
	/** Se facturó más que la venta: hay que revisar. */
	overinvoiced: boolean;
}

/**
 * Resumen de facturación de una orden.
 *
 * El facturado se mide a nivel ORDEN, no sumando el desglose por servicio:
 * `computeBillableServices` solo imputa los ítems que traen `serviceId`, y hay
 * muchas facturas cuyos ítems no lo tienen. Sumando por servicio esas órdenes
 * daban "0% facturado" con facturas emitidas a la vista.
 *
 * Se suman todas las facturas: las notas de crédito vienen en negativo y las
 * anuladas (`Credited`) siguen sumando, así la NC las compensa hasta cero. Es
 * la misma semántica que usa la API para `invoicedAmount`, que es lo que
 * alimenta el listado de órdenes — las dos pantallas dicen lo mismo.
 *
 * Vive acá y no en cada componente porque el cálculo se usa en el home de la
 * orden y en la pestaña de Facturas: tenerlo duplicado ya hizo que se arreglara
 * uno solo de los dos.
 */
export const computeOrderBillingSummary = (services: IServices[], invoices: IInvoice[]): IOrderBillingSummary => {
	const billable = computeBillableServices(services, invoices);
	const total = round2(billable.reduce((acc, s) => acc + s.totalSaleAmount, 0));
	const billed = round2(invoices.reduce((acc, inv) => acc + (inv.totalInMainCurrency || 0), 0));

	return {
		total,
		billed,
		pending: Math.max(0, round2(total - billed)),
		pct: total > 0 ? Math.min(100, Math.round((billed / total) * 100)) : 0,
		overinvoiced: total > 0 && billed > total + OVERINVOICED_TOLERANCE,
	};
};
